var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from "react";
import { Box, Grid, Skeleton } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "@ui/components/header/PageHeader";
import { PageContainer, PageContent } from "@ui/components/wraper";
import { FInput } from "@ui/components/inputs/FInput";
import { SButton } from "@ui/components/buttons/SButton";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { toast } from "@ui/utils/toaster";
import { usePermissions } from "@ui/hooks/usePermissions";
import { Permission } from "@utility/permission-checker";
import { categoriesApi } from "@api/enpoints/categories-api";
import { apiErrorMessage } from "@api/enpoints/clients-api";
/** Create / edit an AP category (/categories/ap/...) or a node category (/categories/node/...). */
const AddEditCategoryPage = () => {
    var _a, _b;
    const { t } = useTranslation(["common", "validation"]);
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const { kind: kindParam, categoryId } = useParams();
    const kind = kindParam === "node" ? "node" : "ap";
    const isEdit = !!categoryId;
    const backPath = kind === "node" ? "/categories?tab=node" : "/categories";
    const [loadingCategory, setLoadingCategory] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: { name: "", description: "" },
    });
    useEffect(() => {
        if (!categoryId)
            return;
        let active = true;
        setLoadingCategory(true);
        categoriesApi
            .getById(kind, categoryId)
            .then((response) => {
            var _a;
            const category = response.data.data;
            if (active && category)
                reset({ name: category.name, description: (_a = category.description) !== null && _a !== void 0 ? _a : "" });
        })
            .catch((error) => {
            toast.error(apiErrorMessage(error, t("common:catalog.categories.loadFailed", "Could not load the category.")));
            navigate(backPath);
        })
            .finally(() => active && setLoadingCategory(false));
        return () => {
            active = false;
        };
    }, [categoryId, kind]);
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        if (saving)
            return;
        setSaving(true);
        try {
            const command = { id: categoryId, name: data.name.trim(), description: data.description.trim() || null };
            if (isEdit)
                yield categoriesApi.update(kind, command);
            else
                yield categoriesApi.create(kind, command);
            toast.success(isEdit
                ? t("common:catalog.categories.updated", "Category updated successfully.")
                : t("common:catalog.categories.created", "Category created successfully."));
            navigate(backPath);
        }
        catch (error) {
            // The form keeps its values; the API message says what to fix (e.g. a duplicate name).
            toast.error(apiErrorMessage(error, t("common:catalog.categories.saveFailed", "Could not save the category.")));
        }
        finally {
            setSaving(false);
        }
    });
    const onDelete = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!categoryId)
            return;
        setDeleting(true);
        try {
            yield categoriesApi.remove(kind, categoryId);
            toast.success(t("common:catalog.categories.deleted", "Category deleted."));
            navigate(backPath);
        }
        catch (error) {
            // 409 when the category is still assigned to access points / nodes.
            toast.error(apiErrorMessage(error, t("common:catalog.categories.deleteFailed", "Could not delete the category.")));
        }
        finally {
            setDeleting(false);
            setConfirmDelete(false);
        }
    });
    const title = isEdit
        ? kind === "ap" ? t("common:catalog.categories.editAp", "Edit AP Category") : t("common:catalog.categories.editNode", "Edit Node Category")
        : kind === "ap" ? t("common:catalog.categories.addAp", "Add AP Category") : t("common:catalog.categories.addNode", "Add Node Category");
    const actionButton = {
        label: isEdit ? t("common:actions.saveChanges", "Save Changes") : title,
        onClick: handleSubmit(onSubmit),
        variant: "contained",
        size: "large",
        disabled: saving || loadingCategory,
        loading: saving,
    };
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: title, showBackButton: true, bottomActionButton: actionButton, topActions: isEdit && hasPermission(Permission.CATEGORIES_DELETE) ? (React.createElement(SButton, { variant: "soft", color: "error", onClick: () => setConfirmDelete(true), disabled: loadingCategory }, t("common:actions.delete", "Delete"))) : undefined }),
        React.createElement(PageContent, { hasBottomAction: true }, loadingCategory ? (React.createElement(Box, null,
            React.createElement(Skeleton, { variant: "rounded", height: 56, sx: { mb: 2 } }),
            React.createElement(Skeleton, { variant: "rounded", height: 96 }))) : (React.createElement("form", { onSubmit: handleSubmit(onSubmit) },
            React.createElement(Grid, { container: true, spacing: 2 },
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(FInput, { name: "name", control: control, label: t("common:catalog.categories.name", "Category Name"), placeholder: t("common:catalog.categories.namePlaceholder", "e.g. Cold Kitchen"), error: errors.name, helperText: (_a = errors.name) === null || _a === void 0 ? void 0 : _a.message, rules: {
                            validate: (v) => v.trim().length > 0 || t("common:catalog.categories.nameRequired", "Category name is required."),
                            maxLength: { value: 128, message: t("common:catalog.categories.nameTooLong", "Maximum 128 characters.") },
                        } })),
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(FInput, { name: "description", control: control, label: t("common:catalog.categories.descriptionOptional", "Description (Optional)"), placeholder: t("common:catalog.categories.descriptionPlaceholder", "Please provide detail about category"), multiline: true, minRows: 3, error: errors.description, helperText: (_b = errors.description) === null || _b === void 0 ? void 0 : _b.message, rules: { maxLength: { value: 512, message: t("common:catalog.categories.descriptionTooLong", "Maximum 512 characters.") } } })))))),
        React.createElement(ConfirmDialog, { isOpen: confirmDelete, onClose: () => setConfirmDelete(false), onConfirm: onDelete, loading: deleting, actionText: t("common:actions.delete", "Delete"), screenName: t("common:catalog.categories.category", "category"), variant: "delete" })));
};
export default AddEditCategoryPage;
